/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import {
  createContext,
  useState,
  type ReactNode,
  use,
  type Dispatch,
  type SetStateAction,
  useRef,
  type RefObject,
} from 'react';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { localStorage } from '@/utils/localStorage';
import root from 'window-or-global';
import type { CustomerLookupResponse } from '@/types';
import { useLoginConfirmation } from '@/features/account/hooks/useLoginConfirmation';
import type { StandaloneOnlineMergeModalsType } from '../features/mergeRewards/interface';

type LinkRewardsTypeProvider = {
  rewardsNumber: string;
  globalError: string;
  lookUpResult?: CustomerLookupResponse;
  searchFieldsRef: RefObject<string[]>;
  handleBack: VoidFunction;
  clearModal: () => void;
  setRewardsNumber: (rewardsNumber: string) => void;
  setGlobalError: (error?: string | null) => void;
  setLookUpResult: Dispatch<SetStateAction<CustomerLookupResponse | undefined>>;
};

export const LinkRewardsContext = createContext<LinkRewardsTypeProvider>(
  {} as LinkRewardsTypeProvider,
);

export const useLinkRewardsContext = () => {
  const context = use(LinkRewardsContext);
  if (context === undefined) {
    throw new Error('useLinkRewardsContext must be used within a LinkRewardsTypeProvider');
  }
  return context;
};

type LinkRewardsProviderProps = {
  children: ReactNode;
};

export const LinkRewardsProvider = ({ children }: LinkRewardsProviderProps) => {
  const [globalError, setGlobalError] = useState<any>(null);
  const [rewardsNumber, setRewardsNumber] = useState('');
  const [lookUpResult, setLookUpResult] = useState<CustomerLookupResponse | undefined>(undefined);
  const searchFieldsRef = useRef<string[]>([]);

  const { loginConfirmationState, showRewardSigninPopup } = useLoginConfirmation();
  const { modalType } = loginConfirmationState;

  const clearModal = () => {
    const { pageName, pageType, siteSection } = root.utag_data ?? {};

    clickTrack({
      eventType: 'rewardsBannerInteraction',
      loginFLow: 'Standalone Online',
      customLinkName: 'Skip for now',
      pageName,
      pageType,
      siteSection,
    });

    setLookUpResult(undefined);
    setRewardsNumber('');
    setGlobalError(null);
    searchFieldsRef.current = [];

    localStorage.setItem('hideModal', 'true');
  };

  const handleBack = () => {
    if (modalType?.flow !== 'SHOW_REWARD_SIGNIN') {
      return;
    }
    switch (modalType.view) {
      case 'LINK_REWARDS_ACC':
      case 'SIGN_ME_UP_FORM':
        showRewardSigninPopup('SHOW_REWARD_SIGNIN');
        break;
      case 'ENTER_REWARDS_ID_FORM':
      case 'LINK_REWARDS_ACC_FORM':
        showRewardSigninPopup('LINK_REWARDS_ACC');
        break;
      case 'SEARCH_CRITERIA_FOUND':
        setLookUpResult(undefined);
        searchFieldsRef.current = [];
        showRewardSigninPopup('LINK_REWARDS_ACC_FORM');
        break;
      case 'VALIDATE_REWARD_QUESTIONS': {
        const view: StandaloneOnlineMergeModalsType =
          lookUpResult?.customers && lookUpResult.customers.length > 0
            ? 'SEARCH_CRITERIA_FOUND'
            : 'ENTER_REWARDS_ID_FORM';
        if (view === 'ENTER_REWARDS_ID_FORM') {
          setRewardsNumber('');
        }
        showRewardSigninPopup(view);
        break;
      }
    }
    setGlobalError(null);
  };

  return (
    <LinkRewardsContext
      value={{
        globalError,
        rewardsNumber,
        lookUpResult,
        searchFieldsRef,
        clearModal,
        handleBack,
        setRewardsNumber,
        setGlobalError,
        setLookUpResult,
      }}
    >
      {children}
    </LinkRewardsContext>
  );
};
